import React, { useState } from 'react';
import {
    ContactPhone as ContactIcon,
    Event as AppointmentIcon,
    EmojiPeople as LeadIcon,
    Receipt as QuotationIcon,
    DoneAll as ClosingIcon,
} from '@mui/icons-material';
import {
    Box,
    Typography,
    TextField,
    Tabs,
    Tab,
    Grid,
    Card,
    CardContent,
    Switch,
    FormControlLabel,
    Button,
    Alert,
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Bar, Doughnut } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    BarElement,
    CategoryScale,
    LinearScale,
    Tooltip,
    Legend,
    ArcElement,
} from 'chart.js';
import { getCrmSummary } from '../../actions/prospections';
import { handleError, handleSuccess, handleWarning } from '../../helpers/utils';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Tooltip,
    Legend,
    ArcElement
);

const colorStyles = {
    red: { background: '#FFCDD2', color: '#B71C1C' },
    orange: { background: '#FFE0B2', color: '#E65100' },
    yellow: { background: '#FFF9C4', color: '#F9A825' },
    blue: { background: '#BBDEFB', color: '#1E88E5' },
    green: { background: '#C8E6C9', color: '#2E7D32' },
};

const typeMapping = {
    '#FFCDD2': 'contact',
    '#FFE0B2': 'appointment',
    '#FFF9C4': 'lead',
    '#BBDEFB': 'quotation',
    '#C8E6C9': 'closing',
};

const cardMapping = {
    Cliente: 'contact',
    Cita: 'appointment',
    Lead: 'lead',
    Cotizacion: 'quotation',
    Cierre: 'closing',
};

const chartOptions = {
    responsive: true,
    scales: {
        y: {
            beginAtZero: true,
            max: 100,
            title: {
                display: true,
                text: 'Cantidad y % de Avance',
            },
        },
    },
    plugins: {
        legend: {
            position: 'top',
        },
    },
};

const iconMapping = {
    contact: <ContactIcon sx={{ fontSize: '50px', color: '#B71C1C' }} />,
    appointment: (
        <AppointmentIcon sx={{ fontSize: '50px', color: '#E65100' }} />
    ),
    lead: <LeadIcon sx={{ fontSize: '50px', color: '#F9A825' }} />,
    quotation: <QuotationIcon sx={{ fontSize: '50px', color: '#1E88E5' }} />,
    closing: <ClosingIcon sx={{ fontSize: '50px', color: '#2E7D32' }} />,
};

export const PendingProspecting = () => {
    const [crmData, setCrmData] = useState(null);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const [showGraphs, setShowGraphs] = useState(true);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [loading, setLoading] = useState(false);

    const fetchCrmSummary = () => {
        if (!startDate || !endDate) {
            handleWarning('Por favor, selecciona ambas fechas para continuar.');
            return;
        }

        setLoading(true);
        const dateRange = `${startDate.toISOString().split('T')[0]}__${
            endDate.toISOString().split('T')[0]
        }`;

        getCrmSummary(null, dateRange)
            .then(response => {
                setCrmData(response.data.data);
                handleSuccess('Datos obtenidos exitosamente.');
            })
            .catch(err => {
                handleError(err, 'Error al obtener los datos.');
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const clearDates = () => {
        setStartDate(null);
        setEndDate(null);
        setCrmData(null);
    };

    const handleTabChange = (event, newValue) => setActiveTab(newValue);

    return (
        <Box sx={{ padding: 3 }}>
            <Grid
                container
                alignItems="center"
                justifyContent="space-between"
                spacing={2}
                sx={{ mb: 3 }}
            >
                <Grid item>
                    <Typography variant="h5" gutterBottom>
                        Prospecciones Pendientes
                    </Typography>
                </Grid>
                <Grid item>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={showGraphs}
                                onChange={() => setShowGraphs(!showGraphs)}
                            />
                        }
                        label={
                            showGraphs ? 'Ocultar Gráficas' : 'Mostrar Gráficas'
                        }
                    />
                </Grid>
            </Grid>

            <Grid container alignItems="center" spacing={2} sx={{ mb: 2 }}>
                <Grid item>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                            label="Fecha de inicio"
                            value={startDate}
                            onChange={newValue => setStartDate(newValue)}
                            renderInput={params => <TextField {...params} />}
                        />
                    </LocalizationProvider>
                </Grid>
                <Grid item>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                            label="Fecha de fin"
                            value={endDate}
                            onChange={newValue => setEndDate(newValue)}
                            renderInput={params => <TextField {...params} />}
                        />
                    </LocalizationProvider>
                </Grid>
                <Grid item>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={fetchCrmSummary}
                        sx={{ mr: 2 }}
                    >
                        Obtener datos
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={clearDates}
                    >
                        Limpiar fechas
                    </Button>
                </Grid>
            </Grid>

            {!startDate || !endDate ? (
                <Alert severity="info" sx={{ mt: 2 }}>
                    Por favor, selecciona ambas fechas para continuar.
                </Alert>
            ) : loading ? (
                <Typography variant="body1" sx={{ mt: 2 }}>
                    Cargando datos...
                </Typography>
            ) : crmData ? (
                <Grid container spacing={2}>
                    {showGraphs && (
                        <Grid item xs={12} md={6}>
                            <Tabs
                                value={activeTab}
                                onChange={handleTabChange}
                                centered
                            >
                                <Tab label="Base de Datos y En Proceso" />
                                <Tab label="Cantidad por Etapas" />
                                <Tab label="Porcentaje de Avance" />
                            </Tabs>
                            <Box sx={{ height: 400, width: '100%', mt: 3 }}>
                                {activeTab === 0 && (
                                    <Bar
                                        data={{
                                            labels: [
                                                'Base de Datos',
                                                'En Proceso',
                                            ],
                                            datasets: [
                                                {
                                                    label: 'Cantidad',
                                                    data: [
                                                        crmData.total_in_db ||
                                                            0,
                                                        crmData.in_progress ||
                                                            0,
                                                    ],
                                                    backgroundColor: [
                                                        '#424242',
                                                        '#546E7A',
                                                    ],
                                                    borderColor: [
                                                        '#424242',
                                                        '#546E7A',
                                                    ],
                                                    borderWidth: 1,
                                                },
                                            ],
                                        }}
                                        options={chartOptions}
                                    />
                                )}
                                {activeTab === 1 && (
                                    <Doughnut
                                        data={{
                                            labels: [
                                                'Contacto',
                                                'Cita',
                                                'Lead',
                                                'Cotización',
                                                'Cierre',
                                            ],
                                            datasets: [
                                                {
                                                    label: 'Cantidad',
                                                    data: [
                                                        crmData.contact
                                                            ?.count || 0,
                                                        crmData.appointment
                                                            ?.count || 0,
                                                        crmData.lead?.count ||
                                                            0,
                                                        crmData.quotation
                                                            ?.count || 0,
                                                        crmData.closing
                                                            ?.count || 0,
                                                    ],
                                                    backgroundColor: [
                                                        colorStyles.red
                                                            .background,
                                                        colorStyles.orange
                                                            .background,
                                                        colorStyles.yellow
                                                            .background,
                                                        colorStyles.blue
                                                            .background,
                                                        colorStyles.green
                                                            .background,
                                                    ],
                                                    borderColor: '#ffffff',
                                                    borderWidth: 2,
                                                },
                                            ],
                                        }}
                                        options={chartOptions}
                                    />
                                )}
                                {activeTab === 2 && (
                                    <Bar
                                        data={{
                                            labels: [
                                                'Contacto',
                                                'Cita',
                                                'Lead',
                                                'Cotización',
                                                'Cierre',
                                            ],
                                            datasets: [
                                                {
                                                    label: 'Porcentaje de Avance',
                                                    data: [
                                                        crmData.contact
                                                            ?.percentage || 0,
                                                        crmData.appointment
                                                            ?.percentage || 0,
                                                        crmData.lead
                                                            ?.percentage || 0,
                                                        crmData.quotation
                                                            ?.percentage || 0,
                                                        crmData.closing
                                                            ?.percentage || 0,
                                                    ],
                                                    backgroundColor: [
                                                        colorStyles.red
                                                            .background,
                                                        colorStyles.orange
                                                            .background,
                                                        colorStyles.yellow
                                                            .background,
                                                        colorStyles.blue
                                                            .background,
                                                        colorStyles.green
                                                            .background,
                                                    ],
                                                    borderColor: [
                                                        colorStyles.red.color,
                                                        colorStyles.orange
                                                            .color,
                                                        colorStyles.yellow
                                                            .color,
                                                        colorStyles.blue.color,
                                                        colorStyles.green.color,
                                                    ],
                                                    borderWidth: 1,
                                                },
                                            ],
                                        }}
                                        options={chartOptions}
                                    />
                                )}
                            </Box>
                        </Grid>
                    )}
                    <Grid item xs={12} md={showGraphs ? 6 : 12}>
                        <Grid
                            container
                            spacing={2}
                            justifyContent={
                                showGraphs ? 'flex-start' : 'space-between'
                            }
                        >
                            {Object.keys(cardMapping).map((title, index) => {
                                const etapa = cardMapping[title];
                                const color = Object.keys(typeMapping).find(
                                    key => typeMapping[key] === etapa
                                );
                                return (
                                    <Grid
                                        item
                                        xs={showGraphs ? 6 : 2.4}
                                        key={index}
                                    >
                                        <Card
                                            sx={{
                                                backgroundColor: color,
                                                color: colorStyles[etapa]
                                                    ?.color,
                                                cursor: 'pointer',
                                            }}
                                        >
                                            <CardContent
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent:
                                                        'space-between',
                                                }}
                                            >
                                                <Box>
                                                    <Typography variant="h6">
                                                        {title}
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        Cantidad:{' '}
                                                        {crmData[etapa]
                                                            ?.count || 0}
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        Avance:{' '}
                                                        {crmData[etapa]
                                                            ?.percentage || 0}
                                                        %
                                                    </Typography>
                                                </Box>
                                                {iconMapping[etapa]}
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    </Grid>
                </Grid>
            ) : null}
        </Box>
    );
};
